import React, { useEffect, useState, useCallback } from 'react';
// Editor Sync Trigger: Aktif
import type { LearningFolder, Branch } from '../../types/db';
import { GradeLevel } from '../../types/db';
import { fetchLearningFolders } from '../../services/academicService';
import {
  BookOpen,
  Plus,
  FileText,
  Trash2,
  Upload,
  X,
  Loader2,
  Building2,
  FileUp,
  FolderOpen,
} from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';

export function ModuleManager({ branchId }: { branchId?: string }) {
  const [folders, setFolders] = useState<LearningFolder[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [manualDriveLink, setManualDriveLink] = useState('');

  const [formData, setFormData] = useState({
    grade: GradeLevel.SMA_12_IPA as string,
  });

  const loadData = useCallback(async () => {
    setFetching(true);
    try {
      const data = await fetchLearningFolders(branchId);
      setFolders(data);
      const { data: bData } = await supabase.from('branches').select('*');
      if (bData) setBranches(bData.filter((b) => b.id !== 'HQ' && b.id !== 'HEADQUARTERS'));
    } catch (err: any) {
      /* console removed */
    } finally {
      setFetching(false);
    }
  }, [branchId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleUpload = async () => {
    if (!formData.grade || !manualDriveLink.trim()) {
      return window.alert('Mohon pilih kelas dan masukkan Google Drive Folder ID.');
    }

    setLoading(true);
    try {
      const moduleId = 'MOD-' + Date.now();
      const folderId = manualDriveLink.trim();

      if (!folderId) {
        throw new Error('Masukkan Folder ID.');
      }

      const folderUrl = `https://drive.google.com/drive/folders/${folderId}`;

      // Save to learning_folders table
      const { error: dbError } = await supabase.from('learning_folders').insert({
        id: moduleId,
        title: formData.grade.toUpperCase(),
        subject: 'GENERAL',
        grade: formData.grade,
        branch_id: 'ALL',
        folder_id: folderId,
        folder_url: folderUrl,
        is_active: true,
      });

      if (dbError) throw dbError;

      window.alert('Folder Belajar Berhasil Ditambahkan!');
      setIsFormOpen(false);
      setFormData({ grade: GradeLevel.SMA_12_IPA as string });
      setManualDriveLink('');
      loadData();
    } catch (err: any) {
      window.alert('Gagal Menambahkan: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (module: LearningFolder) => {
    if (!window.confirm('Hapus folder ini secara permanen dari sistem?')) return;

    try {
      const { error: dbError } = await supabase
        .from('learning_folders')
        .delete()
        .eq('id', module.id);
      if (dbError) throw dbError;

      loadData();
    } catch (err: any) {
      window.alert('Gagal Menghapus: ' + err.message);
    }
  };

  return (
    <div className="space-y-4 animate-fade-in font-sans">
      {/* GOOGLE DRIVE STORAGE NOTIFICATION & STATUS PANEL */}
      <div className="mb-2">
        <div className="p-4 bg-indigo-50 border border-indigo-100 rounded-3xl shadow-sm">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-indigo-600 text-white rounded-2xl flex items-center justify-center shadow-md">
              <Upload className="w-5 h-5" />
            </div>
            <div>
              <p className="text-[10px] font-black uppercase text-indigo-700 tracking-wider">
                Gunakan Link Google Drive Manual
              </p>
              <p className="text-[9px] text-slate-500 uppercase font-semibold mt-1">
                Masukkan link folder atau file Google Drive yang sudah di-share. Tidak perlu upload
                file lewat bucket.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-indigo-600 text-white rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-100">
            <BookOpen className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-xl font-black text-gray-900 uppercase tracking-tight">
              E-Modules Center
            </h2>
            <p className="text-[9px] text-gray-400 font-bold uppercase tracking-widest mt-1">
              Pusat Materi Digital Terintegrasi
            </p>
          </div>
        </div>
        <button
          onClick={() => setIsFormOpen(true)}
          className="w-full md:w-auto bg-indigo-600 text-white px-6 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest shadow-xl hover:bg-indigo-700 transition-all flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" /> Upload Modul Baru
        </button>
      </div>

      {fetching ? (
        <div className="py-20 flex flex-col items-center justify-center gap-4">
          <Loader2 className="animate-spin text-indigo-600 w-10 h-10" />
          <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">
            Sinkronisasi Materi...
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {folders.map((m: LearningFolder) => (
            <div
              key={m.id}
              className="p-5 bg-white border border-slate-100 rounded-3xl flex items-center justify-between group hover:shadow-xl transition-all border-b-4 border-b-indigo-500/10"
            >
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-10 h-10 bg-rose-50 text-rose-500 rounded-xl flex items-center justify-center shrink-0 shadow-inner">
                  <FileText className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <h4 className="font-black text-slate-800 text-[11px] uppercase truncate">
                    {m.title}
                  </h4>
                  <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest truncate mt-0.5">
                    {m.subject} • {m.grade}
                  </p>
                </div>
              </div>
              <div className="flex gap-1 ml-2 opacity-40 group-hover:opacity-100 transition-opacity">
                <a
                  href={m.folder_url}
                  target="_blank"
                  rel="noreferrer"
                  className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
                  title="Buka Folder"
                >
                  <FolderOpen className="w-4 h-4" />
                </a>
                <button
                  onClick={() => handleDelete(m)}
                  className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all"
                  title="Hapus"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}

          {folders.length === 0 && (
            <div className="col-span-full py-32 text-center flex flex-col items-center bg-slate-50/50 rounded-[3rem] border-4 border-dashed border-slate-100">
              <FileUp className="w-12 h-12 text-slate-200 mb-4" />
              <p className="text-slate-300 font-black uppercase tracking-[0.3em] text-[10px]">
                Belum Ada Modul Terunggah Di Unit Ini
              </p>
            </div>
          )}
        </div>
      )}

      {isFormOpen && (
        <div className="fixed inset-0 bg-slate-900/60 z-[200] flex items-center justify-center p-4 backdrop-blur-md">
          <div className="bg-white rounded-[3rem] w-full max-w-md p-10 shadow-2xl animate-fade-in-up border border-white relative max-h-[90vh] overflow-y-auto no-scrollbar">
            <button
              onClick={() => setIsFormOpen(false)}
              className="absolute top-8 right-8 p-2 text-slate-300 hover:text-slate-900 transition-all hover:bg-slate-50 rounded-full"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-inner">
                <Upload className="w-8 h-8" />
              </div>
              <h3 className="font-black text-2xl uppercase tracking-tighter text-slate-900">
                Upload Modul
              </h3>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-2">
                Materi Belajar Digital Delta
              </p>
            </div>

            <div className="space-y-4">
              <div className="space-y-1">
                <label
                  htmlFor="grade-select"
                  className="text-[9px] font-black text-indigo-600 uppercase ml-2 tracking-widest"
                >
                  Pilih Kelas
                </label>
                <select
                  id="grade-select"
                  name="grade"
                  className="w-full border-2 border-slate-50 p-4 rounded-2xl font-black bg-slate-50/50 text-[10px] outline-none"
                  value={formData.grade}
                  onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                >
                  {Object.values(GradeLevel).map((g) => (
                    <option key={g} value={g}>
                      {g.toUpperCase()}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <label
                  htmlFor="manual-drive-link"
                  className="text-[9px] font-black text-indigo-600 uppercase ml-2 tracking-widest"
                >
                  Google Drive Folder ID
                </label>
                <input
                  id="manual-drive-link"
                  name="manualDriveLink"
                  className="w-full border-2 border-slate-50 p-4 rounded-2xl outline-none font-bold text-xs bg-slate-50/50"
                  placeholder="Contoh: 1AbCdEfGhijkLmNoPQrStUvWxYz"
                  value={manualDriveLink}
                  onChange={(e) => setManualDriveLink(e.target.value)}
                />
                <p className="text-[8px] text-slate-400">
                  Masukkan Google Drive File ID.
                  <br />
                  Contoh: https://drive.google.com/file/d/1AbCdEfGhijkLmNoPQr/view
                  <br />
                  Yang dimasukkan hanya: 1AbCdEfGhijkLmNoPQr
                </p>
              </div>

              <button
                onClick={handleUpload}
                disabled={loading}
                className="w-full bg-indigo-600 text-white py-5 rounded-[2rem] font-black uppercase tracking-[0.2em] shadow-2xl flex justify-center items-center gap-3 hover:bg-indigo-700 transition-all mt-4 text-[10px] active:scale-95 disabled:opacity-50"
              >
                {loading ? (
                  <Loader2 className="animate-spin w-5 h-5" />
                ) : (
                  <Upload className="w-5 h-5" />
                )}
                {loading ? 'SEDANG MENGUNGGAH...' : 'KONFIRMASI UPLOAD'}
              </button>

              <button
                onClick={() => setIsFormOpen(false)}
                className="w-full text-slate-300 font-black uppercase tracking-widest text-[8px] py-2 hover:text-slate-500 transition-colors"
              >
                Batalkan
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
